import Tooltip from "@/v2-ui/tooltip"
import Dot, { DotText } from "@/v2-backoffice-client/components/dot"
import type { SharingRiskDetailFragment } from "@/v2-backoffice-client/signIn/__types__/signIn.detail.fragment"
import { getSharingRiskVariant } from "@/v2-backoffice-client/signIn/sharingRisk/sharingRisk.constants"
import * as utils from "@/v2-backoffice-client/signIn/sharingRisk/sharingRisk.utils"
import SharingRiskSessions from "@/v2-backoffice-client/signIn/sharingRisk/SharingRiskSessions"

export type SharingRiskBadgeProps = {
  risk: SharingRiskDetailFragment | null | undefined
}

// Like SharingRiskCard, this lists the backend's own breakdown. Only the
// signals that actually scored are shown, since a tooltip has no room for nine
// rows of zeroes.
function SharingRiskBadge(props: SharingRiskBadgeProps) {
  const { risk } = props
  const level = risk?.Level ?? 1
  const variant = getSharingRiskVariant(level)
  const name = risk?.LevelName ?? "None"

  const scored = (risk?.Signals ?? []).filter((signal) => signal.Points > 0)

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <DotText variant={variant}>{name}</DotText>
      </Tooltip.Trigger>

      <Tooltip.Content
        side="top"
        className="sharing-risk-tooltip ui-tooltip-content--panel"
      >
        <p className="font-medium text-popover-foreground flex items-center gap-1.5">
          <Dot variant={variant} /> {name} - Score: {risk?.Score ?? 0}
        </p>

        {risk?.LevelDescription && (
          <p className="text-muted-foreground text-xs pl-3">
            {risk.LevelDescription}
          </p>
        )}

        {risk?.ScoredSource && (
          <p className="text-muted-foreground text-xs pl-3">
            Scored on worst app: {risk.ScoredSource}
          </p>
        )}

        {(risk?.WindowDays ?? 0) > 0 && (
          <p className="text-muted-foreground text-xs pl-3">
            {utils.formatWindow(
              risk?.WindowDays ?? 0,
              risk?.ThresholdScale ?? 1,
            )}
          </p>
        )}

        {/* Two different empty states. Rows in the historic Events tab carry
            only the user's latest stored level (storedRiskDetail), with no
            score and no breakdown, so "nothing scored" would be a lie there. */}
        {scored.length === 0 &&
          ((risk?.Signals.length ?? 0) === 0 ? (
            <p className="text-muted-foreground text-xs mt-2">
              Latest stored level. Open the user for the signal breakdown.
            </p>
          ) : (
            <p className="text-muted-foreground text-xs mt-2">
              No signal scored in this period.
            </p>
          ))}

        {scored.map((signal) => (
          <div key={signal.Key}>
            <p className="font-medium mt-2">
              {signal.Count} {signal.Label.toLowerCase()}
              <small className="font-normal text-muted-foreground text-xs ml-1">
                ({utils.formatPts(signal.Points)})
              </small>
              :
            </p>
            {signal.Key === "concurrentSessions" ? (
              <SharingRiskSessions
                className="mt-1 pl-2"
                sessions={risk?.ConcurrentSessionPairs}
              />
            ) : signal.Values.length > 0 ? (
              <ul className="text-muted-foreground pl-2">
                {signal.Values.map((value) => (
                  <li key={value}>- {value}</li>
                ))}
              </ul>
            ) : (
              signal.Note && (
                <p className="text-muted-foreground text-xs pl-2">
                  {signal.Note}
                </p>
              )
            )}
          </div>
        ))}

        {(risk?.TotalDistinctIpCount ?? 0) > (risk?.DistinctIpCount ?? 0) && (
          <p className="text-muted-foreground text-xs mt-2">
            Across all apps: {risk?.TotalDistinctIpCount} IPs ·{" "}
            {risk?.TotalDistinctLocationCount} locations ·{" "}
            {risk?.TotalDistinctDeviceCount} devices (not scored — the score is
            the worst single app)
          </p>
        )}

        <hr className="sharing-risk-tooltip-divider" />

        <div className="sharing-risk-tooltip-scale">
          <p className="font-medium text-xs mb-1">Score thresholds</p>
          <div className="flex flex-col gap-1 text-xs pl-2">
            <DotText variant="secondary" className="text-popover-foreground">
              0–1 None
            </DotText>
            <DotText variant="info" className="text-popover-foreground">
              2–4 Low
            </DotText>
            <DotText variant="warning" className="text-popover-foreground">
              5–7 Moderate
            </DotText>
            <DotText variant="orange" className="text-popover-foreground">
              8–12 High
            </DotText>
            <DotText variant="destructive" className="text-popover-foreground">
              13+ Critical
            </DotText>
          </div>
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default SharingRiskBadge
